import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Message } from "@/i18n/Message";
import { t, useLocale } from "@/i18n/locale";
import SurfaceState, { type SurfaceStateAction } from "./SurfaceState";

/**
 * Launch feedback keeps the launch's *error* moments:
 *
 * - `failed`: the launch timed out or was rejected. The surface switches to an
 *   error view here.
 * - offline: the connection is gone, so waiting cannot finish. Its own state,
 *   never confused with the end of a Try.
 *
 * The loading UI itself no longer lives here — the surface shows one Default
 * Splash (AppSplash) plus the header progress bar from the launch request until
 * the app is operable, so a phase change never swaps one spinner for another.
 *
 * The hook still separates three moments: admission (`loading`/`slow`), first
 * paint (`revealed`) and readiness (`ready`). Readiness and visibility stay
 * deliberately different signals.
 */
type Phase = "idle" | "loading" | "slow" | "revealed" | "external" | "ready" | "failed";

/** Admission and the first admitted document each get a bounded loading
 *  window. The deadline only fails a launch while nothing is visible: once
 *  the app has painted, a late `load` is not a failed launch. */
export function useAppLaunchFeedback(identity: string, active: boolean, failed: boolean, onFailure: () => void, surfaceUrl: string | null = null) {
  const [snapshot, setSnapshot] = useState({ identity, phase: "idle" as Phase, started: 0, surfaceStarted: null as number | null });
  const current = useRef(snapshot);
  const latestIdentity = useRef(identity);
  latestIdentity.current = identity;
  const failure = useRef(onFailure);
  failure.current = onFailure;
  const publish = useCallback((phase: Phase) => {
    if (latestIdentity.current !== identity) return;
    const previous = current.current;
    const next = { identity, phase, started: previous.identity === identity ? previous.started : 0,
      surfaceStarted: previous.identity === identity ? previous.surfaceStarted : null };
    current.current = next;
    setSnapshot(next);
  }, [identity]);
  const fail = useCallback(() => {
    if (latestIdentity.current !== identity) return;
    if (current.current.identity === identity && current.current.phase === "failed") return;
    publish("failed");
    failure.current();
  }, [identity, publish]);
  /** First paint of the app document. Never resurrects a failed attempt. */
  const reveal = useCallback(() => {
    if (latestIdentity.current !== identity) return;
    const same = current.current.identity === identity;
    const phase = same ? current.current.phase : "idle";
    if (phase === "failed" || phase === "ready" || phase === "revealed") return;
    if (!active && phase === "idle") return;
    const started = same && current.current.started ? current.current.started : Date.now();
    const surfaceStarted = same ? current.current.surfaceStarted : null;
    const next = { identity, phase: "revealed" as Phase, started, surfaceStarted };
    current.current = next;
    setSnapshot(next);
  }, [identity, active]);
  const ready = useCallback(() => {
    if (latestIdentity.current !== identity) return;
    // A late frame must never resurrect a timed-out attempt.
    if (current.current.identity === identity && current.current.phase === "failed") return;
    publish("ready");
  }, [identity, publish]);
  // Choosing the live app's own tab transfers loading to that tab. An iframe
  // timeout must not reclaim a Try the visitor explicitly chose to use there.
  // This is not evidence of first paint/readiness; the Try's own expiry remains.
  const openExternal = useCallback(() => {
    if (!active || !surfaceUrl || latestIdentity.current !== identity) return;
    if (current.current.identity === identity && ["failed", "ready", "revealed", "external"].includes(current.current.phase)) return;
    publish("external");
  }, [identity, active, surfaceUrl, publish]);
  useEffect(() => {
    if (failed) { fail(); return; }
    if (!active) return;
    const previous = current.current;
    if (previous.identity === identity && ["ready", "failed", "external"].includes(previous.phase)) return;
    const started = previous.identity === identity && previous.started ? previous.started : Date.now();
    const surfaceStarted = (previous.identity === identity ? previous.surfaceStarted : null) ?? (surfaceUrl ? Date.now() : null);
    // A reveal that landed before this effect (the iframe can paint before
    // passive effects run) must survive activation.
    const phase = previous.identity === identity && previous.phase === "revealed"
      ? "revealed"
      : Date.now() - started >= 3_000 ? "slow" : "loading";
    current.current = { identity, phase, started, surfaceStarted };
    setSnapshot(current.current);
    const elapsed = Date.now() - started;
    const slow = setTimeout(() => {
      if (current.current.identity === identity && current.current.phase === "loading") publish("slow");
    }, Math.max(0, 3_000 - elapsed));
    // Admission and the admitted document have separate windows. A document
    // that has started keeps loading on a slow connection instead of being
    // discarded at the old 15s mark (measured: 298 KB on 400 kbps needs
    // ~15s+ just for the app graph); the hard cap still bounds an attempt
    // that never paints.
    const deadline = surfaceStarted === null
      ? started + 15_000
      : Math.min(started + 60_000, surfaceStarted + 45_000);
    const timeout = setTimeout(() => {
      if (current.current.identity !== identity) return;
      if (["loading", "slow"].includes(current.current.phase)) fail();
    }, Math.max(0, deadline - Date.now()));
    return () => { clearTimeout(slow); clearTimeout(timeout); };
  }, [identity, active, failed, publish, fail, surfaceUrl]);
  const phase: Phase = failed ? "failed" : snapshot.identity === identity && snapshot.phase !== "idle" ? snapshot.phase : active ? "loading" : "idle";
  return { phase, reveal, ready, fail, openExternal };
}

export default function AppLaunchFeedback({ phase, offline = false, errorMessage, retryable = true, retryAt = 0, externalUrl, onRetry, onBack, onReturnToTry, children }: {
  phase: Phase;
  /** Connection is gone. Only rendered as a state when the surface has not
   *  already become usable — never over a running app. */
  offline?: boolean;
  errorMessage?: string;
  retryable?: boolean;
  retryAt?: number;
  externalUrl?: string | null;
  onReturnToTry?: () => void;
  onRetry: () => void;
  onBack: () => void;
  children: ReactNode;
}) {
  useLocale();
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    setNow(Date.now());
    if (!retryAt || retryAt <= Date.now()) return;
    const timer = setInterval(() => { const time = Date.now(); setNow(time); if (time >= retryAt) clearInterval(timer); }, 250);
    return () => clearInterval(timer);
  }, [retryAt]);
  const retrySeconds = Math.max(0, Math.ceil((retryAt - now) / 1_000));
  const waiting = retrySeconds > 0;

  // A lost connection is why the launch cannot finish. Show that state before
  // any failure caused by the loss, but never hide a permanent failure (e.g.
  // the app was removed) behind it — retrying cannot bring that app back.
  if (offline && phase !== "ready" && phase !== "external" && !(phase === "failed" && !retryable)) {
    return <div className="app-launch-surface" aria-busy={false}>
      <SurfaceState
        className="app-launch-feedback app-launch-feedback--offline"
        actionsClassName="app-launch-actions"
        icon="offline"
        role="status"
        live="polite"
        title={<Message message="You're offline" />}
        description={<Message message="The app will start when the connection returns." />}
        actions={[
          { label: <Message message="Reconnect" />, kind: "primary", onClick: onRetry },
          { label: <Message message="Back to app list" />, kind: "secondary", onClick: onBack },
        ]}
      />
    </div>;
  }

  if (phase === "failed") {
    const actions: SurfaceStateAction[] = [];
    if (onReturnToTry) actions.push({ label: <Message message="Return to running Try" />, kind: "primary", onClick: onReturnToTry });
    if (retryable) actions.push({
      label: waiting ? t("Retry in {{value0}}s", { value0: retrySeconds }) : t("Retry app"),
      kind: onReturnToTry ? "secondary" : "primary",
      disabled: waiting,
      onClick: () => { if (Date.now() >= retryAt) onRetry(); },
    });
    if (externalUrl) actions.push({ label: <Message message="Open in new tab" />, href: externalUrl, kind: "secondary" });
    actions.push({ label: <Message message="Back to app list" />, kind: retryable || onReturnToTry ? "secondary" : "primary", onClick: onBack });
    return <div className="app-launch-surface" aria-busy={false}>
      <SurfaceState
        className="app-launch-feedback app-launch-feedback--failed"
        actionsClassName="app-launch-actions"
        icon={waiting ? "clock" : "alert"}
        role="alert"
        live="assertive"
        title={errorMessage === "App not found. It may have been removed."
          ? <Message message="App not found" />
          : waiting ? <Message message="Wait before trying again" /> : <Message message="Couldn't open this app" />}
        description={<Message message={errorMessage ?? "The app didn't open. Try again, or go back to the app list."} />}
        actions={actions}
      />
    </div>;
  }

  return <div className="app-launch-surface" aria-busy={phase === "loading" || phase === "slow" || phase === "revealed"}>
    <div className="app-launch-content">{children}</div>
    {phase === "external" ? <div className="app-launch-revealed-status" role="status"><Message message="This app opens in its own tab." /></div> : null}
  </div>;
}
